import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Beat } from "../../../../plugins/spinners.plugin";
import Card from "../../../../components/cards/admin/cards";
import Button from "../../../../components/buttons/buttons";
import Table from "../../../../components/tables/tableCols1";
import TableSkeleton from "../../../../components/skeleton/table";
import { writeFileWithXLSX } from "../../../../components/exports/xlsx";

import EmptyState from "../../../../assets/images/emptyState/payment.svg";
import { AllFiltersModal } from "../../../../components/modals/allFiltersModal";
import { RequestPayoutModal } from "../../../../components/modals/admin/requestPayoutModal";
import FilterRestaurantOrdersDropDown from "../../../../components/dropDown/filter/filterRestaurantOrders";
import {
  filterRevenuePayouts,
  searchRevenuePayout,
  getAllRevenuePayoutsNoPagination,
} from "../../../../redux/revenuePayout/revenuePayoutActions";
import payoutType from "../../../../redux/revenuePayout/revenuePayoutTypes";
import { getABusiness } from "../../../../redux/business/businessActions";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export default function PayOut() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [isFilterOpen, setFilterState] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [input, setInput] = useState({
    search: "",
  });

  const { allRevenuePayout, downloading, filtering } = useSelector(
    (state) => state.revenuePayout,
  );
  console.log("allRevenuePayout", allRevenuePayout);

  const payLoad = useMemo(
    () => ({
      business_id: id,
      paid_out: 0,
    }),
    [id],
  );

  useEffect(() => {
    dispatch(filterRevenuePayouts(payLoad));
  }, [dispatch, payLoad]);

  // kept locally so a previously viewed business's name never shows
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    let ignore = false;
    setBusiness(null);

    const fetchBusiness = async () => {
      const response = await dispatch(getABusiness(id));
      if (!ignore) setBusiness(response?.data ?? null);
    };

    if (id) fetchBusiness();
    return () => {
      ignore = true;
    };
  }, [dispatch, id]);

  useEffect(() => {
    const dataSet = createTableData(allRevenuePayout?.data);
    setData(dataSet);
  }, [allRevenuePayout?.data]);

  function createTableData(data) {
    if (Array.isArray(data)) {
      const outputArray = [];
      data.forEach((payout, index) => {
        const single = {
          num: <span className="text-base font-semibold">{index + 1}</span>,
          requestId: payout?.request_id || "N/A",
          amount: (
            <span className="text-green-600 dark:text-green-400 font-semibold">
              ₦{numberWithCommas(payout?.amount || 0)}
            </span>
          ),
          requestDate: moment(payout?.request_date).format("DD MMM YYYY"),
          status: (
            <span className="inline-flex rounded-full px-2 py-1 capitalize text-xs font-semibold bg-yellow-100 text-yellow-800">
             {payout?.status}
            </span>
          ),
          payout: payout,
        };

        outputArray.push({ single });
      });

      return outputArray;
    }
    return [];
  }

  function createTableDataDownload(data) {
    let dataToMap = data;
    if (Array.isArray(dataToMap)) {
      let outputArray = [];
      dataToMap.map((order) => {
        let show = {
          id: order?.id,
          food_name: order.food_name,
          discount: order.food_discount,
          amount: order?.food_amount,
          availability: order?.food_operating_status,
          approved: order?.management_approved ? "Approved" : "Unapproved",
        };
        outputArray.push(show);
        return true;
      });
      return outputArray;
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    e.preventDefault();
    setInput({ ...input, [name]: value });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  async function doSearch() {
    try {
      let response = await dispatch(
        searchRevenuePayout({ ...input, restaurant_id: allRevenuePayout?.id }),
      );
      if (response?.status === "success") {
        showSearch(response?.data);
      }
      if (!response?.status) {
        showSearch([]);
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (input?.search?.length === 0) {
      removeSearch();
    }
    // eslint-disable-next-line
  }, [input, dispatch]);

  const handleDownload = async () => {
    const response = await dispatch(
      getAllRevenuePayoutsNoPagination({
        ...payLoad,
        restaurant_id: allRevenuePayout?.id,
        pagination: 0,
      }),
    );
    if (response) {
      const newData = createTableDataDownload(response?.data);
      writeFileWithXLSX(newData, {
        filename: "Restaurant_Orders.xlsx",
        download: true,
      });
    }
  };

  const showSearch = (search) => {
    let dataSet = createTableData(search);
    setData(dataSet);
    setIsSearchOpen(true);
  };

  const removeSearch = () => {
    if (input?.search?.length === 0) {
      let dataSet = createTableData(allRevenuePayout?.data);
      setData(dataSet);
      setIsSearchOpen(false);
      dispatch({
        type: payoutType["SEARCH_REVENUE_PAYOUT"],
        payLoad: { data: [] },
      });
    }
  };

  const goToSinglePage = (params) => {
    const requestId = params?.single?.payout?.id;
    navigate(`/admin/revenue/business/${id}/payout/${requestId}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        hideHeader: false,
        id: "checkbox-table-column",
        columns: [
          {
            Header: "S/N",
            accessor: "single.num",
          },
          {
            Header: "Request ID",
            accessor: "single.requestId",
          },
          {
            Header: "Amount",
            accessor: "single.amount",
          },
          {
            Header: "Request Date",
            accessor: "single.requestDate",
          },
          {
            Header: "Status",
            accessor: "single.status",
          },
        ],
      },
    ],
    [],
  );

  return (
    <div>
      <Card.TableFilter
        title={[
          business?.business_name,
          allRevenuePayout?.total > 0 &&
            `Showing ${allRevenuePayout?.from ?? "0"} - ${
              allRevenuePayout?.to ?? "0"
            } of ${allRevenuePayout?.total ?? "0"} Payouts`,
        ]
          .filter(Boolean)
          .join(" · ")}
        searchPlaceholder={"Search Orders"}
        onChange={handleChange}
        handleKeyDown={handleKeyDown}
        filter={
          <AllFiltersModal
            isFilterOpen={isFilterOpen}
            setFilterState={setFilterState}
          >
            <FilterRestaurantOrdersDropDown
              isFilterOpen={isFilterOpen}
              setFilterState={setFilterState}
              payLoad={payLoad}
            />
          </AllFiltersModal>
        }
      >
        <RequestPayoutModal
          ownerId={id}
          type="business"
          onSuccess={() => dispatch(filterRevenuePayouts(payLoad))}
        />
        <Button.Secondary
          title={downloading ? <Beat color={"#ffffff"} /> : "Download CSV"}
          disabled={!data}
          onClick={handleDownload}
        />
      </Card.TableFilter>

      <div className="border-b border-[#E4E4F3] dark:border-[#505050]"></div>

      {filtering ? (
        <TableSkeleton />
      ) : (
        <div className="bg-white dark:bg-[#121212] rounded-md py-4 px-3">
          {data?.length > 0 ? (
            <Table
              columns={columns}
              data={data}
              onClick={goToSinglePage}
              goToSinglePage={goToSinglePage}
            />
          ) : (
            <div className="text-center py-12">
              <img
                src={EmptyState}
                alt="empty"
                className="w-32 h-32 mx-auto mb-4"
              />
              <p className="text-gray-500 dark:text-gray-400">
                {isSearchOpen
                  ? "No payout requests match your search"
                  : "No payout requests found"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
